// modules/RFC_module.js
export default class RFCModule {
  constructor({ qcForm, controller, status = null }) {
    this.qcForm = qcForm;
    this.controller = controller;
    this.status = status;
    this.stackId = this.controller.gradingFormStackId;
    this.userId = this.controller.gradingFormUserId;
    this.rfcHistory = this.controller?.gradingRfcHistory;
    this.adjRfcHistory = this.controller?.adjudicationRfcHistory;
    this.qcRfcHistory = this.controller?.qcRfcHistory;
    this.fetchGradingFormIdByStackId();
  }
  handleReasonForChange(form) {
    // Get the changed component key from the form data
    const changedComponentKey = this.getChangedComponentKey(form);
    if (changedComponentKey) {
      // Generate the corresponding RFC key
      const rfcKey = `RFC_${changedComponentKey}`;
      // Add small delay to ensure form processing is complete
      setTimeout(() => {
        this.showRFCComponent(this.qcForm, rfcKey);
      }, 0);
    }
  }

  // Method to handle form status changes
  handleStatusChange(formData = null, qcResponses = null) {
    if (this.status === "completed") {
      // Hide all RFC fields when form is completed
      this.hideAllRFCFields(formData);
    } else if (this.status === "editing") {
      // In editing mode, hide all RFC fields initially
      // They will only show when user makes changes via handleReasonForChange
      this.hideAllRFCFields(formData);
    }

    // Check if form is disabled and hide RFC fields
    if (this.qcForm && this.qcForm.disabled) {
      this.hideAllRFCFields(formData);
    }
  }

  // Method to hide all RFC fields
  hideAllRFCFields(formData = null) {
    if (!formData?.components) return;

    const allComponents = this.getAllComponents(formData.components);

    allComponents.forEach((component) => {
      const key = component.key || component.component?.key;
      if (key && key.startsWith("RFC_")) {
        const instance = this.qcForm?.getComponent(key);
        this.setComponentVisibility(component, false, {
          skipRefresh: !instance?.visible,
        });
      }
    });
  }

  // NEW: Method to show RFC fields that already have values

  showRFCFieldsWithValues(formData = null, qcResponses = null) {
    let responseData = {};

    // Parse and extract response data
    if (qcResponses) {
      let data = qcResponses.data || qcResponses || {};

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse response data:", e);
          data = {};
        }
      }

      responseData = data.data || data || {};
    }

    // Check if formData and components exist
    if (
      !formData ||
      !formData.components ||
      !Array.isArray(formData.components)
    ) {
      console.warn("Invalid formData structure or no components found");
      return;
    }

    const allComponents = this.getAllComponents(formData.components);

    allComponents.forEach((component, componentIndex) => {
      try {
        const key = component.key || component.component?.key;
        if (!key || !key.startsWith("RFC_")) return;

        const hasValidValue = this.hasValidResponseValue(responseData, key);
        this.setComponentVisibility(component, hasValidValue, {
          skipRefresh: true,
        });
      } catch (componentError) {
        console.error(
          `Error processing component ${componentIndex}:`,
          componentError,
        );
      }
    });
  }

  resolveComponentInstance(component, componentKey = null) {
    const key =
      componentKey ||
      component?.key ||
      component?.component?.key ||
      (component?.component && component.component.key);

    if (key && this.qcForm?.getComponent) {
      const instance = this.qcForm.getComponent(key);
      if (instance) return { instance, key, schema: instance.component };
    }

    if (component?.component) {
      return { instance: component, key, schema: component.component };
    }

    if (component?.key) {
      return { instance: null, key: component.key, schema: component };
    }

    return { instance: null, key: null, schema: null };
  }

  setComponentVisibility(component, show = false, { skipRefresh = false } = {}) {
    if (!component) {
      console.warn("No component provided to setComponentVisibility");
      return;
    }

    try {
      const { instance, key: componentKey, schema } =
        this.resolveComponentInstance(component);

      if (!schema || !componentKey) {
        console.warn("Component schema not found for visibility update");
        return;
      }

      const wasHidden = !!schema.hidden;
      schema.hidden = !show;

      if (instance && !skipRefresh && (show || wasHidden !== !show)) {
        this.refreshComponent(instance, show);
      }

      // ALWAYS add audit icon to parent field regardless of RFC field visibility
      if (componentKey.startsWith("RFC_")) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            this.addAuditIconToRFCField(
              { key: componentKey, component: instance || component },
              this.auditLogValues,
            );
          });
        }, 100);
      }
    } catch (error) {
      console.error(
        `Error setting component visibility for ${component.key}:`,
        error,
      );
    }
  }

  // Update a single Form.io component's visibility without redrawing the whole form.
  refreshComponent(componentInstance, show = null) {
    try {
      if (!componentInstance) return;

      const shouldShow =
        show !== null ? show : !componentInstance.component?.hidden;

      if (typeof componentInstance.checkComponentConditions === "function") {
        componentInstance.checkComponentConditions();
      }

      if (componentInstance.visible !== shouldShow) {
        componentInstance.visible = shouldShow;
      } else if (typeof componentInstance.redraw === "function") {
        componentInstance.redraw();
      }
    } catch (error) {
      console.error("Error refreshing component:", error);
    }
  }

  // Alternative method if you need to show/hide all components at once
  showAllRFCFields(formData = null, qcResponses = null) {
    const responseData = this.parseResponseData(qcResponses);

    if (!formData?.components) return;

    const componentsToProcess = this.getAllComponents(formData.components);

    componentsToProcess.forEach((component) => {
      // Only process components that start with RFC_
      if (component.key.startsWith("RFC_")) {
        const hasValidValue = this.hasValidResponseValue(
          responseData,
          component.key,
        );
        this.setComponentVisibility(component, hasValidValue);
      }
    });
  }

  // Helper method to parse response data
  parseResponseData(qcResponses) {
    let responseData = {};

    if (qcResponses) {
      let data = qcResponses.data || qcResponses || {};

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Failed to parse response data:", e);
          data = {};
        }
      }

      responseData = data.data || data || {};
    }

    return responseData;
  }

  // Helper method to get all components (including nested ones)
  getAllComponents(components) {
    const allComponents = [];

    components.forEach((component) => {
      if (component.components && Array.isArray(component.components)) {
        // Add nested components
        allComponents.push(...component.components);
        // Recursively get deeply nested components
        allComponents.push(...this.getAllComponents(component.components));
      } else {
        // Add direct component
        allComponents.push(component);
      }
    });

    return allComponents;
  }

  // Helper method to check if response has valid value
  hasValidResponseValue(responseData, rfcKey) {
    return (
      responseData.hasOwnProperty(rfcKey) &&
      responseData[rfcKey] !== null &&
      responseData[rfcKey] !== undefined &&
      responseData[rfcKey].toString().trim() !== ""
    );
  }

  // NEW: Helper method to find all RFC components
  findAllRFCComponents(formComponent, callback) {
    if (formComponent.components && Array.isArray(formComponent.components)) {
      formComponent.components.forEach((component) => {
        if (component.components && Array.isArray(component.components)) {
          component.components.forEach((question) => {
            if (question.key && question.key.startsWith("RFC_")) {
              callback(question, question.key);
            }
          });
        }
      });
    }
  }

  getChangedComponentKey(changedSubmission) {
    const changedComponent =
      changedSubmission?.changed?.component ||
      changedSubmission?.flags?.changed?.component;

    const key =
      changedComponent?.key ||
      changedComponent?.component?.key ||
      (changedComponent?.component && changedComponent.component.key);

    if (!key || typeof key !== "string") return null;
    // Ignore changes on RFC fields themselves
    if (key.startsWith("RFC_")) return null;

    return key;
  }

  showRFCComponent(_formComponent, rfcKey) {
    const componentInstance = this.qcForm?.getComponent(rfcKey);
    if (componentInstance) {
      this.setComponentVisibility(componentInstance, true);
      this.clearRFCFieldValue({
        key: rfcKey,
        component: componentInstance,
      });
      return;
    }

    const allComponents = this.getAllComponents(this.qcForm?.components || []);
    const schemaComponent = allComponents.find(
      (component) =>
        (component.key || component.component?.key) === rfcKey,
    );
    if (schemaComponent) {
      this.setComponentVisibility(schemaComponent, true);
      this.clearRFCFieldValue(schemaComponent);
    }
  }

  // Updated method to clear RFC field value
  clearRFCFieldValue(component) {
    try {
      const { instance: componentInstance, key: componentKey } =
        this.resolveComponentInstance(component);

      if (componentInstance) {
        // Method 1: Try setValue if it exists
        if (
          componentInstance.setValue &&
          typeof componentInstance.setValue === "function"
        ) {
          componentInstance.setValue("", { noUpdateEvent: true });
        }
        // Method 2: Try setting value property directly
        else if (componentInstance.hasOwnProperty("value")) {
          componentInstance.value = "";
        }
        // Method 3: Try setting data property
        else if (componentInstance.data && componentKey) {
          componentInstance.data[componentKey] = "";
        }

        // Always clear from form data
        if (this.qcForm.data && componentKey) {
          this.qcForm.data[componentKey] = "";
        }

        // Method 4: Clear DOM input elements directly
        if (componentInstance.element) {
          const inputs = componentInstance.element.querySelectorAll(
            "input, textarea, select",
          );
          inputs.forEach((input) => {
            input.value = "";
          });
        }

      }
    } catch (error) {
      const fallbackKey =
        component?.key ||
        component?.component?.key ||
        (component?.component && component.component.key);
      console.error(
        `Error clearing RFC field value for ${fallbackKey}:`,
        error,
      );
      // Fallback: Just clear from form data
      try {
        if (this.qcForm.data && fallbackKey) {
          this.qcForm.data[fallbackKey] = "";
        }
      } catch (fallbackError) {
        console.error("Fallback clear also failed:", fallbackError);
      }
    }
  }
  forceFormRedraw() {
    // Try multiple redraw methods with different timings
    const redrawMethods = [
      () => this.qcForm.redraw && this.qcForm.redraw(),
      () => this.qcForm.refresh && this.qcForm.refresh(),
      () => this.qcForm.rebuild && this.qcForm.rebuild(),
      () => this.qcForm.triggerRedraw && this.qcForm.triggerRedraw(),
    ];
    // Immediate redraw
    redrawMethods.forEach((method) => {
      try {
        method();
      } catch (error) {
        // Silently continue if method doesn't exist
      }
    });
    // Delayed redraw to ensure changes take effect
    setTimeout(() => {
      redrawMethods.forEach((method) => {
        try {
          method();
        } catch (error) {
          // Silently continue if method doesn't exist
        }
      });
    }, 10);
    // Another delayed redraw as final fallback
    setTimeout(() => {
      if (this.qcForm.redraw && typeof this.qcForm.redraw === "function") {
        this.qcForm.redraw();
      }
    }, 100);
  }

  // Method to hide RFC component when needed
  hideRFCComponent(formComponent, rfcKey) {
    let componentFound = false;
    if (formComponent.components && Array.isArray(formComponent.components)) {
      formComponent.components.forEach((component) => {
        if (component.components && Array.isArray(component.components)) {
          component.components.forEach((question) => {
            if (question.key === rfcKey) {
              this.hideComponent(question);
              componentFound = true;
            }
          });
        }
      });
    }
  }

  hideComponent(component) {
    const { instance, schema } = this.resolveComponentInstance(component);
    if (!schema) return;

    schema.hidden = true;
    if (schema.validate) {
      schema.validate.required = false;
    }
    if (instance) {
      this.refreshComponent(instance, false);
    }
  }

  addAuditIconToRFCField(component, auditLogValues) {
    try {
      const key =
        component.key || (component.component && component.component.key);
      if (!key || !key.startsWith("RFC_")) return;

      // Get the parent field key by removing "RFC_" prefix
      const parentKey = key.replace("RFC_", "");

      // Get the parent component instance instead of the RFC component
      const componentInstance = this.qcForm.getComponent(parentKey);
      if (!componentInstance || !componentInstance.element) return;

      const injectIcon = () => {
        const labelElement =
          componentInstance.element.querySelector(".control-label") ||
          componentInstance.element.querySelector(".col-form-label") ||
          componentInstance.element.querySelector("label") ||
          componentInstance.element.querySelector(".form-label");

        if (!labelElement || labelElement.querySelector(".rfc-audit-icon"))
          return;

        // Check if we already have a container, if not create one
        let container = labelElement.querySelector(".rfc-icon-container");

        if (!container) {
          container = document.createElement("span");
          container.classList.add("rfc-icon-container");
          container.style.display = "inline-flex";
          container.style.alignItems = "center";
          container.style.gap = "5px";

          while (labelElement.firstChild) {
            container.appendChild(labelElement.firstChild);
          }

          labelElement.appendChild(container);
        }

        if (!container.querySelector(".rfc-audit-icon")) {
          const getRFCFieldHistory = (rfcKey) => {
            if (!this.auditLogValues || !Array.isArray(this.auditLogValues))
              return [];

            return this.auditLogValues
              .map((entry) => entry[rfcKey])
              .filter(
                (v) =>
                  v &&
                  typeof v === "object" &&
                  v.value !== undefined &&
                  v.user_id,
              )
              .map((v) => ({
                value: v.value,
                user_id: v.user_id,
              }));
          };

          // 🧠 Tooltip formatter to include user IDs
          const formatTooltipContent = (displayKey, historyEntries) => {
            if (!Array.isArray(historyEntries) || historyEntries.length === 0) {
              return `<div class="text-gray-500">${displayKey}: No history found</div>`;
            }

            const uniqueEntries = [];
            const seen = new Set();

            for (const entry of historyEntries) {
              const hash = `${entry.value}-${entry.user_id}`;
              if (!seen.has(hash)) {
                uniqueEntries.push(entry);
                seen.add(hash);
              }
            }

            // Reverse the array so most recent is first
            const reversedEntries = uniqueEntries.reverse();

            const formattedValues = reversedEntries
              .map((entry, index) => {
                // Most recent is now at index 0
                const isMostRecent = index === 0;

                return `
    <div class="${isMostRecent ? "bg-green-100 text-green-800 font-bold" : "bg-gray-100 text-gray-800"} 
                px-2 py-1 rounded border border-gray-200 text-sm">
      <span class="italic">${entry.value}</span> - ${entry.user_id}
    </div>
  `;
              })
              .join("");

            return `<div class="flex flex-col gap-1">${formattedValues}</div>`;
          };

          // Create and append the SVG icon with tooltip
          const icon = document.createElement("span");
          icon.classList.add("has-tooltip", "rfc-audit-icon");

          const rfcHistoryEntries = getRFCFieldHistory(key);

          icon.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#007BFF" viewBox="0 0 24 24" style="vertical-align: middle;">
          <path d="M19 3h-4.18A3 3 0 0 0 12 1a3 3 0 0 0-2.82 2H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zM12 3a1 1 0 1 1-1 1 1 1 0 0 1 1-1zm7 18H5V5h2v2h10V5h2z"/>
          <path d="M12 7a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 2a1 1 0 0 1 1 1v2.28l1.4.72a1 1 0 0 1-.9 1.8l-2-1A1 1 0 0 1 11 13V10a1 1 0 0 1 1-1z"/>
        </svg>
        <span class="tooltip bottom-2 bg-white text-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 text-xs min-w-max max-w-sm">
          ${formatTooltipContent(parentKey, rfcHistoryEntries)}
        </span>
      `;

          container.appendChild(icon);
        }
      };

      injectIcon();

      // Re-inject only if Form.io rebuilds the label (e.g. after a panel redraw).
      const labelSelector =
        ".control-label, .col-form-label, label, .form-label";
      const labelElementForObserver =
        componentInstance.element.querySelector(labelSelector);
      if (!labelElementForObserver) return;

      const observer = new MutationObserver(() => {
        if (!componentInstance.element?.isConnected) {
          observer.disconnect();
          return;
        }
        if (!labelElementForObserver.querySelector(".rfc-audit-icon")) {
          injectIcon();
        }
      });

      observer.observe(labelElementForObserver, {
        childList: true,
      });
    } catch (error) {
      console.error("Failed to add RFC icon:", error);
    }
  }

  fetchGradingFormIdByStackId() {
    this.stackId =
      this.controller?.stackId || this.controller?.gradingFormStackId;
    if (!this.stackId) {
      console.error("Stack ID is missing.");
      return;
    }
    let historyUrl = null;

    // Determine form context
    if (this.rfcHistory) {
      historyUrl = this.rfcHistory; // Grading form
    } else if (this.adjRfcHistory) {
      historyUrl = this.adjRfcHistory; // Adjudication form
    } else if (this.qcRfcHistory) {
      historyUrl = this.qcRfcHistory; // QC form
    } else {
      // No RFC history URL found - this is expected for some form types
      return;
    }

    // Validate URL before fetching
    if (!historyUrl || historyUrl === "null" || historyUrl.includes("null")) {
      // Invalid RFC history URL - silently skip
      return;
    }

    fetch(historyUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Error fetching grading form: ${response.statusText}`,
          );
        }
        return response.json();
      })
      .then((data) => {
        if (data?.id || data?.grading_form_id) {
          const gradingFormId = data.id || data.grading_form_id;
          this.gradingFormId = gradingFormId;

          if (Array.isArray(data.audit_logs)) {
            const extractedRFCValues = data.audit_logs.map((entry) => {
              const userId = entry.user_id;
              const responsesData = entry.new_values?.responses?.data || {};

              const rfcFields = {};
              for (const key in responsesData) {
                if (/^RFC_/.test(key)) {
                  rfcFields[key] = {
                    value: responsesData[key],
                    user_id: userId,
                  };
                }
              }
              return rfcFields;
            });

            this.auditLogValues = extractedRFCValues;

            const rfcComponents = this.getAllComponents(
              this.qcForm?.components || [],
            ).filter((component) => {
              const key = component.key || component.component?.key;
              return key && key.startsWith("RFC_");
            });
            rfcComponents.forEach((component) => {
              this.addAuditIconToRFCField(component, this.auditLogValues);
            });
          } else {
            console.warn("⚠️ No audit logs (new_values) received.");
          }
        } else {
          console.warn("❌ No grading form found for this stack ID.");
        }
      })
      .catch((error) => {
        console.error("❌ Error fetching grading form:", error);
      });
  }

  formatKey(key) {
    // Converts camelCase or snake_case to Human Readable form
    return key
      .replace(/[_\-]/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  formatRFC(history) {
    try {
      const parsed = JSON.parse(history);
      if (parsed.old !== undefined && parsed.new !== undefined) {
        return `Previous: ${parsed.old}\nUpdated: ${parsed.new}`;
      }
    } catch (e) {
      // Fallback if not JSON
    }
    return history;
  }
}
